/**
 * @voltxampere/engine — B-source expression DSL evaluator
 * (Sprint 54.2b-4)
 *
 * Walks the AST and computes a scalar value. Reads V(net) / I(comp)
 * via injected context lookups (so the engine stamp can pass current
 * Newton iterate values without coupling this module to MNA
 * internals).
 *
 * Built-in functions:
 *   - V(net) / I(component) — reserved 1-arg AST forms
 *   - sqrt(x), abs(x), min(a,b), max(a,b) — math
 *   - pulse(v1, v2, td, tr, tf, pw, per) — SPICE time-domain pulse
 *
 * Conditional truth: comparison nodes return 0 or 1; conditional
 * nodes treat anything > 0 as truthy.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Evaluate an Expr AST against the given context. Throws
 * ContractViolationError on unknown function or arity mismatch.
 */
export function evaluate(expr, ctx) {
    switch (expr.kind) {
        case "literal":
            return expr.value;
        case "nodeRef": {
            const v = ctx.nodeVoltage(expr.net);
            if (!Number.isFinite(v)) {
                throw new ContractViolationError(`dsl/evaluator: V(${expr.net}) returned non-finite (net unknown?)`);
            }
            return v;
        }
        case "componentRef": {
            const i = ctx.componentCurrent(expr.id);
            if (!Number.isFinite(i)) {
                throw new ContractViolationError(`dsl/evaluator: I(${expr.id}) returned non-finite (component unknown?)`);
            }
            return i;
        }
        case "negate":
            return -evaluate(expr.operand, ctx);
        case "binary": {
            const l = evaluate(expr.left, ctx);
            const r = evaluate(expr.right, ctx);
            switch (expr.op) {
                case "+":
                    return l + r;
                case "-":
                    return l - r;
                case "*":
                    return l * r;
                case "/":
                    if (r === 0) {
                        throw new ContractViolationError("dsl/evaluator: division by zero");
                    }
                    return l / r;
            }
            // exhaustive — unreachable
            return 0;
        }
        case "comparison": {
            const l = evaluate(expr.left, ctx);
            const r = evaluate(expr.right, ctx);
            let result = false;
            switch (expr.op) {
                case "<":
                    result = l < r;
                    break;
                case ">":
                    result = l > r;
                    break;
                case "<=":
                    result = l <= r;
                    break;
                case ">=":
                    result = l >= r;
                    break;
                case "==":
                    result = l === r;
                    break;
                case "!=":
                    result = l !== r;
                    break;
            }
            return result ? 1 : 0;
        }
        case "conditional": {
            const test = evaluate(expr.test, ctx);
            // Truthy: > 0. SPICE convention.
            if (test > 0) {
                return evaluate(expr.consequent, ctx);
            }
            return evaluate(expr.alternate, ctx);
        }
        case "functionCall": {
            const args = expr.args.map((a) => evaluate(a, ctx));
            return callBuiltin(expr.name, args, ctx);
        }
    }
}
/**
 * Dispatch table for builtin function calls. Throws on unknown
 * function or wrong arity.
 */
function callBuiltin(name, args, ctx) {
    const lower = name.toLowerCase();
    switch (lower) {
        case "sqrt":
            requireArity(name, args, 1);
            return Math.sqrt(args[0] ?? 0);
        case "abs":
            requireArity(name, args, 1);
            return Math.abs(args[0] ?? 0);
        case "min":
            requireArity(name, args, 2);
            return Math.min(args[0] ?? 0, args[1] ?? 0);
        case "max":
            requireArity(name, args, 2);
            return Math.max(args[0] ?? 0, args[1] ?? 0);
        case "pulse": {
            requireArity(name, args, 7);
            const [v1, v2, td, tr, tf, pw, per] = args;
            return pulseValueAt(ctx.time, v1, v2, td, tr, tf, pw, per);
        }
        default:
            throw new ContractViolationError(`dsl/evaluator: unknown function '${name}'`);
    }
}
function requireArity(name, args, expected) {
    if (args.length !== expected) {
        throw new ContractViolationError(`dsl/evaluator: ${name}() expects ${String(expected)} args, got ${String(args.length)}`);
    }
}
/**
 * SPICE PULSE(v1, v2, td, tr, tf, pw, per) evaluated at time t.
 *
 *   Period:
 *     [td, td+tr]            : ramp from v1 to v2
 *     [td+tr, td+tr+pw]      : hold at v2
 *     [td+tr+pw, td+tr+pw+tf]: ramp from v2 to v1
 *     [td+tr+pw+tf, td+per]  : hold at v1
 *
 * Before `td`: returns v1. DC OP (time = 0): returns v1 too (idle
 * state) — ngspice DC analysis takes pulse_source as v1.
 *
 * Periodic: t > td+per wraps via `(t - td) mod per`.
 */
export function pulseValueAt(t, v1, v2, td, tr, tf, pw, per) {
    if (t <= td)
        return v1;
    // Modular periodic time.
    const elapsed = t - td;
    const phase = per > 0 ? elapsed % per : elapsed;
    if (phase < tr) {
        // Rising ramp from v1 to v2.
        if (tr === 0)
            return v2;
        return v1 + ((v2 - v1) * phase) / tr;
    }
    if (phase < tr + pw) {
        return v2;
    }
    if (phase < tr + pw + tf) {
        // Falling ramp from v2 to v1.
        if (tf === 0)
            return v1;
        return v2 + ((v1 - v2) * (phase - tr - pw)) / tf;
    }
    return v1;
}
//# sourceMappingURL=evaluator.js.map